'use client';

import { useSessionStore } from '@insta-club/state';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAccountSwitcherSheetStore } from 'src/libs/state/stores/accountSwitcherSheet';
import { useMenuItems } from './useMenuItems';

const BottomNavigation = () => {
    const pathName = usePathname();
    const includedPaths = [
        '/dashboard',
        '/analysis',
        '/explore',
        '/academy',
        '/profile',
        '/support',
        '/support/tickets',
        '/subscription',
        '/videos',
        '/profile/favorite',
        '/privacy',
        '/terms',
        '/faq',
        '/wallet',
        '/wallet/deposit',
        '/tour',
        '/profile/history/analysis'
    ];
    const isIncludedPaths = includedPaths.includes(pathName);

    const openAccountSwitcherSheet = useAccountSwitcherSheetStore((state) => state.openDrawer);
    const hasNoPages = useSessionStore((state) => state.hasNoPages);
    const activePageDetail = useSessionStore((state) => state.page);

    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const holdDuration = 500;

    const handleHoldStart = useCallback(() => {
        timerRef.current = setTimeout(() => {
            openAccountSwitcherSheet();
        }, holdDuration);
    }, [openAccountSwitcherSheet]);

    const handleHoldEnd = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const Menus = useMenuItems({
        hasNoPages,
        activePageDetail,
        handleHoldStart,
        handleHoldEnd
    });

    const [active, setActive] = useState(0);
    const navRef = useRef<HTMLDivElement>(null);
    const liRefs = useRef<HTMLAnchorElement[]>([]);

    const holeX = useMotionValue(0);
    const holeXSpring = useSpring(holeX, { stiffness: 150, damping: 15 });
    const holeXSpringPx = useTransform(holeXSpring, (x) => `${x}px`);

    useEffect(() => {
        const idx = Menus.findIndex((m) => pathName.startsWith(m.href));
        if (idx !== -1) setActive(idx);
    }, [pathName, Menus]);

    useLayoutEffect(() => {
        let rafId = 0;
        let mounted = true;

        const updateHolePosition = () => {
            if (!mounted) return;

            if (liRefs.current[active] && navRef.current) {
                const rect = liRefs.current[active].getBoundingClientRect();
                const navRect = navRef.current.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2 - navRect.left;
                holeX.set(centerX);
            } else {
                rafId = requestAnimationFrame(updateHolePosition);
            }
        };

        updateHolePosition();
        window.addEventListener('resize', updateHolePosition);

        return () => {
            mounted = false;
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updateHolePosition);
        };
    }, [active, holeX]);

    if (!isIncludedPaths) return null;

    return (
        <div className='fixed bottom-0 z-[39] w-full max-w-[var(--container-max)]'>
            <div
                className='absolute bottom-0 left-0 w-full'
                style={{ filter: 'drop-shadow(0px -4px 26px rgba(0, 0, 0, 0.06))' }}
            >
                <motion.div
                    ref={navRef}
                    className='absolute bottom-0 left-0 w-full rounded-t-xl bg-white px-6'
                    style={{
                        WebkitMaskImage: `radial-gradient(circle 16px at var(--hole-x, 50px) 0%, transparent 99%, black 100%)`,
                        WebkitMaskRepeat: 'no-repeat',
                        WebkitMaskSize: '100% 100%',
                        maskImage: `radial-gradient(circle 16px at var(--hole-x, 50px) 0%, transparent 99%, black 100%)`,
                        maskRepeat: 'no-repeat',
                        maskSize: '100% 100%',
                        ['--hole-x' as string]: holeXSpringPx
                    }}
                >
                    <ul className='relative flex'>
                        {Menus.map((menu, i) => (
                            <Link
                                href={menu.href}
                                key={i}
                                className='flex-1 text-center'
                                ref={(el: HTMLAnchorElement | null) => {
                                    if (el) liRefs.current[i] = el;
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                            >
                                <button
                                    type='button'
                                    onClick={() => setActive(i)}
                                    className='flex w-full flex-col items-center justify-center pt-5 pb-2'
                                >
                                    <span
                                        className={`cursor-pointer duration-500 ${
                                            i === active ? 'text-primary-600' : 'text-gray-400'
                                        }`}
                                    >
                                        {menu.icon}
                                    </span>
                                    <span
                                        className={`mt-1 text-xs ${
                                            i === active
                                                ? 'text-primary-600 text-[13px] font-semibold'
                                                : 'font-medium text-gray-400'
                                        }`}
                                    >
                                        {menu.name}
                                    </span>
                                </button>
                            </Link>
                        ))}
                    </ul>
                </motion.div>

                <motion.div
                    className='bg-primary-500 pointer-events-none absolute -top-[77px] h-[13px] w-[13px] rounded-full'
                    style={{
                        left: holeXSpringPx,
                        translateX: '-50%'
                    }}
                />
            </div>
        </div>
    );
};

export default BottomNavigation;

